import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Pressable, View } from 'react-native';

import { Text, useTheme } from '@/components/ui';
import type { InboxAutoReply, PreviousLetterDelivery } from '@/services/data-service';
import { letterDeliveryAttempt } from '@/utils/message-delivery';

/**
 * "An inbox rule answered this by itself" — the pane banner and the list chip.
 *
 * Also the ONE delivery vocabulary for letters a PM composed (#555): a Waiting for Board
 * Approval or needs-info letter recorded but held, or sent to the test mailbox, renders through
 * the same banner and chip with `noun="Letter"` (see `letterDeliveryAttempt`). The auto-reply
 * wording and test ids are the default and unchanged.
 *
 * Standing policy is that no email goes out without a property manager seeing it. Rule
 * auto-replies are the one sanctioned exception, on the condition that every one of them is
 * VERY visible. So this is loud on purpose, sits above the subject rather than in history,
 * and never says "sent" for a letter that did not reach the homeowner: a preview send that
 * went to the test mailbox, a send held by a gate, a failure and a rule that matched but
 * could not write are each named for what they are.
 */

type Outcome = InboxAutoReply['outcome'];

/** Green only for a letter that reached the homeowner; amber for "not what it looks like". */
const OUTCOME_COLOR: Record<Outcome, string> = {
	sent: '#2E7D32',
	sunk: '#B26A00',
	held: '#B26A00',
	failed: '#C62828',
	skipped: '#B26A00',
};

const OUTCOME_ICON: Record<Outcome, keyof typeof Ionicons.glyphMap> = {
	sent: 'arrow-undo',
	sunk: 'flask-outline',
	held: 'pause-circle-outline',
	failed: 'alert-circle',
	skipped: 'remove-circle-outline',
};

/** What the banner and chip are about. Auto-reply is the original, and the default. */
export type DeliveryNoun = 'Auto-reply' | 'Letter';

const CHIP_LABEL: Record<Outcome, string> = {
	sent: 'AUTO-REPLIED',
	sunk: 'AUTO-REPLY (TEST)',
	held: 'AUTO-REPLY HELD',
	failed: 'AUTO-REPLY FAILED',
	skipped: 'AUTO-REPLY SKIPPED',
};

const chipLabel = (outcome: Outcome, noun: DeliveryNoun): string => {
	if (noun === 'Auto-reply') return CHIP_LABEL[outcome];
	const upper = noun.toUpperCase();
	return outcome === 'sunk' ? `${upper} (TEST)` : `${upper} ${outcome === 'held' ? 'NOT DELIVERED' : outcome.toUpperCase()}`;
};

const SKIP_REASON: Record<string, string> = {
	no_template: 'its reply template is missing or disabled',
	no_recipient: 'no email address could be found for this message',
};

/** The banner's headline — exported so the wording is pinned by a test, not by eye. */
export function deliveryHeadline(info: InboxAutoReply, noun: DeliveryNoun = 'Auto-reply'): string {
	const to = info.to.join(', ') || 'the homeowner';
	switch (info.outcome) {
		case 'sent':
			return `${noun} sent to ${to}`;
		case 'sunk':
			return `${noun} went to the test mailbox${info.sunkTo ? ` (${info.sunkTo})` : ''}, not to ${to}`;
		case 'held':
			return `${noun} NOT sent to ${to} — held${info.reason ? `: ${info.reason}` : ''}`;
		case 'failed':
			return `${noun} to ${to} FAILED${info.reason ? `: ${info.reason}` : ''}`;
		case 'skipped':
		default:
			return `An inbox rule matched but NO auto-reply was sent${
				info.reason ? ` — ${SKIP_REASON[info.reason] ?? info.reason}` : ''
			}`;
	}
}

/** The auto-reply wording, as #595 pinned it. */
export const autoReplyHeadline = (info: InboxAutoReply): string => deliveryHeadline(info, 'Auto-reply');

/**
 * The composer's "you already wrote to them" note, worded for what actually happened (#555).
 *
 * Only a letter recorded as `sent` may say it reached the homeowner. A held or test-mailbox one
 * uses the banner's own wording; an unknown state (a row from before migration 272) says only
 * that a letter was recorded — it never claims a delivery nobody recorded.
 */
export function previousLetterNote(
	sentWording: string,
	at: number,
	delivery: PreviousLetterDelivery | null | undefined,
): string {
	const day = new Date(at).toLocaleDateString();
	if (delivery?.status === 'sent') return `${sentWording} on ${day}.`;
	const attempt = letterDeliveryAttempt({
		deliveryStatus: delivery?.status, deliveryNote: delivery?.note, toAddress: delivery?.to,
	});
	if (attempt) return `A letter like this was recorded on ${day}: ${deliveryHeadline(attempt, 'Letter')}.`;
	return `A letter like this was recorded on ${day}.`;
}

/** Rule · template · when. A real moment, so viewer-local is right here. */
const autoReplyDetail = (info: InboxAutoReply): string => {
	const parts: string[] = [];
	if (info.ruleName) parts.push(`Rule "${info.ruleName}"`);
	if (info.templateName) parts.push(`template "${info.templateName}"`);
	if (info.at) parts.push(new Date(info.at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }));
	// An older reply whose letter could not be matched safely. Say so, never guess.
	if (info.detailsUnavailable) parts.push('details unavailable');
	/**
	 * A non-delivery with no parsed reason: the recorded note is the only explanation there
	 * is, so show it verbatim rather than an unexplained "skipped" or "held".
	 */
	const unexplained = info.outcome !== 'sent' && info.outcome !== 'sunk' && !info.reason && !!info.summary;
	if (unexplained) parts.push(info.summary);
	return parts.length ? parts.join(' · ') : info.summary;
};

const AutoReplyBannerRow: React.FC<{
	info: InboxAutoReply; index: number; noun: DeliveryNoun; idPrefix: string;
}> = ({ info, index, noun, idPrefix }) => {
	const theme = useTheme();
	const [open, setOpen] = useState(false);
	const color = OUTCOME_COLOR[info.outcome];
	const reply = info.reply;
	return (
		<View
			testID={`${idPrefix}-banner-${index}`}
			style={{
				paddingVertical: 10,
				paddingHorizontal: 12,
				marginBottom: 10,
				borderRadius: 8,
				borderWidth: 1,
				borderLeftWidth: 5,
				borderColor: color,
				backgroundColor: `${color}14`,
			}}
		>
			<View style={{ flexDirection: 'row', alignItems: 'center' }}>
				<Ionicons name={OUTCOME_ICON[info.outcome]} size={18} color={color} style={{ marginRight: 8 }} />
				<Text
					testID={`${idPrefix}-headline-${index}`}
					style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: '800', color }}
				>
					{deliveryHeadline(info, noun)}
				</Text>
				{/* Gated on the reply existing, not on its body: a template can legitimately render
					blank, and "a blank letter went out" is exactly what someone needs to see. */}
				{reply ? (
					<Pressable
						onPress={() => setOpen((v) => !v)}
						testID={idPrefix === 'message-auto-reply' ? `btn-auto-reply-toggle-${index}` : `btn-${idPrefix}-toggle-${index}`}
						// Real padding: RN-Web drops hitSlop.
						style={{ paddingVertical: 4, paddingHorizontal: 8 }}
					>
						<Text style={{ fontSize: 13, fontWeight: '700', color: theme.colors.primary }}>
							{open ? 'Hide reply' : 'Show reply'}
						</Text>
					</Pressable>
				) : null}
			</View>
			<Text
				testID={`${idPrefix}-detail-${index}`}
				style={{ marginTop: 2, marginLeft: 26, fontSize: 12, color: theme.colors.onSurfaceVariant }}
			>
				{autoReplyDetail(info)}
			</Text>
			{open && reply ? (
				<View
					testID={`${idPrefix}-text-${index}`}
					style={{
						marginTop: 8,
						marginLeft: 26,
						padding: 10,
						borderRadius: 6,
						backgroundColor: theme.colors.surface,
					}}
				>
					<Text style={{ fontWeight: '700', marginBottom: 4 }}>{reply.subject || '(no subject)'}</Text>
					<Text style={{ fontSize: 13, color: theme.colors.onSurface }}>{reply.bodyText || '(empty body)'}</Text>
				</View>
			) : null}
		</View>
	);
};

/**
 * Every rule auto-reply attempt on this message, newest first. Renders nothing when a rule
 * never tried — the absence is itself the answer to "did anything go out on its own?".
 */
export const AutoReplyBanner: React.FC<{
	attempts: InboxAutoReply[] | null | undefined;
	/** `Letter` for a composed letter's own delivery (#555). */
	noun?: DeliveryNoun;
	/** testID stem; the auto-reply ids are pinned by existing specs and stay the default. */
	idPrefix?: string;
}> = ({ attempts, noun = 'Auto-reply', idPrefix = 'message-auto-reply' }) => {
	if (!attempts?.length) return null;
	return (
		<View testID={`${idPrefix}-banner`}>
			{attempts.map((info, i) => (
				<AutoReplyBannerRow key={`${info.at ?? ''}-${i}`} info={info} index={i} noun={noun} idPrefix={idPrefix} />
			))}
		</View>
	);
};

/** The list row's pill — same shape as UNFILED/ARCHIVED beside it. */
export const AutoReplyChip: React.FC<{
	info: InboxAutoReply | null | undefined; testID?: string; noun?: DeliveryNoun;
}> = ({ info, testID, noun = 'Auto-reply' }) => {
	if (!info) return null;
	return (
		<View
			testID={testID}
			style={{
				flexShrink: 0,
				flexDirection: 'row',
				alignItems: 'center',
				paddingHorizontal: 6,
				paddingVertical: 1,
				borderRadius: 4,
				backgroundColor: OUTCOME_COLOR[info.outcome],
			}}
		>
			<Ionicons name={OUTCOME_ICON[info.outcome]} size={10} color="#fff" style={{ marginRight: 3 }} />
			<Text style={{ fontSize: 10, fontWeight: '800', letterSpacing: 0.4, color: '#fff' }}>
				{chipLabel(info.outcome, noun)}
			</Text>
		</View>
	);
};
